import { ChevronRight, X } from "lucide-react";
import Link from "next/link";
import { useEffect, useState } from "react";
import AcademyIcon from "@/components/icons/academy";
import ArticleIcon from "@/components/icons/article";
import CalculatorIcon from "@/components/icons/calculator";
import ForumIcon from "@/components/icons/forum";
import PlayIcon from "@/components/icons/play";
import QuizIcon from "@/components/icons/quiz";
import SearchIcon from "@/components/icons/search";
import ShoppingCartIcon from "@/components/icons/shopping-cart";

const STORAGE_KEY = "bebekcom_recent_searches";

export function getRecentSearches(): string[] {
  if (typeof window === "undefined") {
    return [];
  }
  try {
    const item = window.localStorage.getItem(STORAGE_KEY);
    return item ? JSON.parse(item) : [];
  } catch {
    return [];
  }
}

const MAX_RECENT_SEARCHES = 3;

export function addRecentSearch(term: string) {
  if (!term.trim() || typeof window === "undefined") {
    return;
  }
  const current = getRecentSearches();
  const filtered = current.filter(
    (s) => s.toLowerCase() !== term.toLowerCase(),
  );
  const updated = [term, ...filtered].slice(0, MAX_RECENT_SEARCHES);
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
  window.dispatchEvent(new Event("recentSearchesUpdated"));
}

export function removeRecentSearch(term: string) {
  if (typeof window === "undefined") {
    return;
  }
  const current = getRecentSearches();
  const updated = current.filter((s) => s.toLowerCase() !== term.toLowerCase());
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
  window.dispatchEvent(new Event("recentSearchesUpdated"));
}

export function clearRecentSearches() {
  if (typeof window === "undefined") {
    return;
  }
  window.localStorage.removeItem(STORAGE_KEY);
  window.dispatchEvent(new Event("recentSearchesUpdated"));
}

const PopoverPlayIcon = (props: React.SVGProps<SVGSVGElement>) => (
  <PlayIcon
    {...props}
    className={`${props.className || ""} scale-110`}
    stroke="#0091D5"
  />
);

const PopoverSearchIcon = (props: React.SVGProps<SVGSVGElement>) => (
  <SearchIcon
    {...props}
    className={`${props.className || ""} [&_path]:stroke-current`}
  />
);
const DISCOVER_LINKS = [
  {
    href: "/forum",
    icon: ForumIcon,
    subtitle: "Sor, öğren, paylaş",
    title: "Forum",
  },
  {
    href: "/makaleler",
    icon: ArticleIcon,
    subtitle: "Uzman yazıları",
    title: "Makaleler",
  },
  {
    href: "/tv",
    icon: PopoverPlayIcon,
    subtitle: "Videolar, canlı yayınlar",
    title: "bebek.com TV",
  },
  {
    href: "/alisveris",
    icon: ShoppingCartIcon,
    subtitle: "Karar vermeden önceki rehberin",
    title: "Alışveriş Rehberi",
  },
  {
    href: "/akademi",
    icon: AcademyIcon,
    subtitle: "Atölyeler ve kamplar",
    title: "bebek.com Akademi",
  },
  {
    href: "/quiz",
    icon: QuizIcon,
    subtitle: "Eğlenerek öğrenin",
    title: "Quiz",
  },
  {
    href: "/hesaplama",
    icon: CalculatorIcon,
    subtitle: "Birlikte hesaplayalım",
    title: "Hesaplama Araçları",
  },
];

interface SearchPopoverProps {
  onSelectSearch?: (term: string) => void;
}

export function SearchPopover({ onSelectSearch }: SearchPopoverProps) {
  const [recentSearches, setRecentSearches] = useState<string[]>([]);

  useEffect(() => {
    setRecentSearches(getRecentSearches());
    const handleUpdate = () => setRecentSearches(getRecentSearches());
    window.addEventListener("recentSearchesUpdated", handleUpdate);
    return () =>
      window.removeEventListener("recentSearchesUpdated", handleUpdate);
  }, []);

  return (
    <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-2xl shadow-xl border border-neutral-200 overflow-hidden z-50">
      <div className="pb-2">
        <div className="bg-neutral-50 px-6 py-3 border-y border-neutral-100 flex items-center justify-between">
          <span className="text-[11px] font-bold  uppercase tracking-wider">
            Son Aramalar
          </span>
          {recentSearches.length > 0 && (
            <button
              className="text-[11px] font-bold text-brand-blue-500 hover:text-brand-blue-600 transition-colors cursor-pointer"
              onClick={clearRecentSearches}
              onMouseDown={(e) => e.preventDefault()}
              type="button"
            >
              Temizle
            </button>
          )}
        </div>
        <div className="flex flex-col px-2 pt-2">
          {recentSearches.length === 0 ? (
            <div className="px-4 py-2 text-sm text-neutral-400">
              Henüz arama geçmişiniz bulunmuyor.
            </div>
          ) : (
            recentSearches.map((search, index) => (
              <button
                className={`flex items-center justify-between p-3 hover:bg-neutral-50 rounded-xl transition-colors group w-full cursor-pointer ${
                  index !== recentSearches.length - 1
                    ? "border-b border-neutral-100"
                    : ""
                }`}
                key={search}
                onClick={() => onSelectSearch?.(search)}
                type="button"
              >
                <div className="flex items-center gap-3 text-neutral-600 group-hover:text-neutral-900">
                  <PopoverSearchIcon className="w-5 h-5 text-neutral-400 group-hover:text-brand-blue-500 transition-colors" />
                  <span className="text-sm font-medium">{search}</span>
                </div>
                <button
                  className="p-1 cursor-pointer"
                  onClick={(e) => {
                    e.stopPropagation();
                    removeRecentSearch(search);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.stopPropagation();
                      removeRecentSearch(search);
                    }
                  }}
                  onMouseDown={(e) => {
                    e.preventDefault();

                    e.stopPropagation();
                  }}
                  tabIndex={0}
                  type="button"
                >
                  <X className="w-4 h-4 text-neutral-400 hover:text-neutral-600 transition-colors" />
                </button>
              </button>
            ))
          )}
        </div>
      </div>

      <div className="flex flex-col">
        <div className="bg-neutral-50 px-6 py-2 border-y border-neutral-100">
          <span className="text-[11px] font-bold  uppercase tracking-wider">
            Keşfet
          </span>
        </div>
        <div className="flex flex-col px-2 py-2">
          {DISCOVER_LINKS.map((link, index) => (
            <Link
              className={`flex items-center justify-between p-3 hover:bg-neutral-50 rounded-xl transition-colors group ${
                index !== DISCOVER_LINKS.length - 1
                  ? "border-b border-neutral-100"
                  : ""
              }`}
              href={link.href}
              key={link.title}
            >
              <div className="flex items-center gap-4 text-neutral-600">
                <div>
                  <link.icon />
                </div>
                <div className="flex flex-col">
                  <span className="text-sm font-bold text-neutral-800 group-hover:text-brand-blue-600 transition-colors">
                    {link.title}
                  </span>
                  <span className="text-xs text-neutral-500">
                    {link.subtitle}
                  </span>
                </div>
              </div>
              <ChevronRight className="w-5 h-5 text-neutral-300 group-hover:text-brand-blue-500 transition-colors" />
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}
